import { useEffect, useState, useRef, useContext } from 'react';
import createStore from 'ctx-provider';
import { cloneDeep } from 'lodash';
import authenticationCtx from 'context/authentication';
import useAlert from 'hooks/useAlert';
import { getMarketdata } from 'api/trading-api';
import { REFRESH_TIME_MARKETDATA_MIN } from 'config/constants';
import exchangeCtx from 'context/exchange';

const REFRESH_TIME_MARKETDATA_MS = REFRESH_TIME_MARKETDATA_MIN * 60 * 1000;

const useTradesHistory = () => {
	const { exchange } = useContext(exchangeCtx);
	const alert = useAlert();
	const { isLoggedIn } = useContext(authenticationCtx);
	const [subscriptionActive, setSubscriptionActive] = useState(false);
	const [marketdata, setMarketdata] = useState({});

	const timeoutId = useRef();
	const subscriptionActiveRef = useRef();
	const exchangeRef = useRef({});

	useEffect(() => {
		exchangeRef.current = cloneDeep(exchange);
		cancelTimeout();
		loadData();
	}, [exchange]);

	const subscribe = () => {
		subscriptionActiveRef.current = true;
		setSubscriptionActive(true);
	};

	const unsubscribe = () => {
		subscriptionActiveRef.current = false;
		setSubscriptionActive(false);
	};

	const cancelTimeout = () => {
		if (timeoutId.current) {
			clearTimeout(timeoutId.current);
		}
	};

	const loadData = async () => {
		try {
			if (subscriptionActiveRef.current && exchange) {
				const { name, network } = exchangeRef.current;
				const data = await getMarketdata({ exchange: name, network });
				if (exchangeRef.current.name === name && exchangeRef.current.network === network && data) {
					setMarketdata(cloneDeep(data));
				}
			}
		} catch (err) {
			const message = err.response?.data?.error || err.message || 'Server error';
			// send an alert, but prevent alerting multiple times, by marking this message with "match":
			console.error(err);

			alert(message, { noDupes: true, match: `${message}#error`, severity: 'error' });
		}
		timeoutId.current = setTimeout(loadData, REFRESH_TIME_MARKETDATA_MS);
	};

	useEffect(
		() => () => {
			cancelTimeout();
		},
		[],
	);

	useEffect(() => {
		if (isLoggedIn && subscriptionActive) {
			loadData();
		} else {
			cancelTimeout();
			setMarketdata({});
		}
	}, [isLoggedIn, subscriptionActive]);

	return {
		marketdata,
		subscribe,
		unsubscribe,
	};
};

const store = createStore(useTradesHistory);

export const { Provider } = store;
export default store.ctx;
